import { useCallback, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon } from './icons';
import { useFocusTrap } from './useFocusTrap';

/** Props for {@link HelpDrawer}. */
export interface HelpDrawerProps {
  /** Whether the drawer is open. When false, nothing is rendered. */
  open: boolean;
  /** Called when the user dismisses (Esc, backdrop, or close button). */
  onClose: () => void;
  /** Drawer heading. */
  title: string;
  /** Optional subtitle (e.g. the article category). */
  subtitle?: ReactNode;
  /** Drawer body. */
  children?: ReactNode;
  /** Optional footer (e.g. a link into the Help Centre). */
  footer?: ReactNode;
}

/**
 * Centered modal that renders help content in place without navigation loss
 * (spec 09 §2). Sized to the content (max-w-2xl, capped at 85vh with the body
 * scrolling). Shares the accessible-dialog behaviour of {@link Modal}: rendered in
 * a portal, `role="dialog"` + `aria-modal`, focus trapped and restored on close,
 * dismissible with `Esc` or a backdrop click (see {@link useFocusTrap}).
 *
 * The name is retained for compatibility; it now presents centered rather than as a
 * right-hand slide-over.
 */
export function HelpDrawer({ open, onClose, title, subtitle, children, footer }: HelpDrawerProps): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  const handleEscape = useCallback(() => onClose(), [onClose]);
  useFocusTrap(open, panelRef, handleEscape);

  if (!open) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm dark:bg-black/60"
        aria-hidden="true"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="relative flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl outline-none dark:bg-navy-surface"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4 dark:border-navy-border">
          <div className="min-w-0">
            {subtitle !== undefined ? (
              <p className="text-xs font-medium uppercase tracking-wide text-brand-600 dark:text-brand-300">
                {subtitle}
              </p>
            ) : null}
            <h2 id={titleId} className="mt-0.5 text-base font-semibold text-slate-900 dark:text-slate-100">
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close help"
            className="-m-1 rounded-md p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:text-slate-400 dark:hover:bg-navy-bg dark:hover:text-slate-200"
          >
            <CloseIcon size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer !== undefined ? (
          <div className="border-t border-slate-200 px-5 py-3 dark:border-navy-border">{footer}</div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
